import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store';
import { addNotification } from '../../store/slices/notificationSlice';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { usePageMetadata } from '../../hooks/usePageMetadata';

import { Badge } from '../../components/ui/Badge';
import {
  User,
  Mail,
  Phone,
  Camera,
  CreditCard,
  Building2,
  ArrowLeft
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useGetProfileQuery, useUpdateProfileMutation, useUpdateUserAccountDetailsMutation } from '../../store/slices/apiSlice';
import Navbar from '../../components/common/Navbar';
import UserAvatar from '../../components/common/UserAvatar';

const profileSchema = yup.object({
  name: yup.string().required('Name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  country_code: yup.string().optional(),
});

const bankAccountSchema = yup.object({
  account_holder_name: yup.string().required('Account holder name is required'),
  bank_name: yup.string().required('Bank name is required'),
  account_number: yup.string().required('Account number is required'),
});

type ProfileFormData = yup.InferType<typeof profileSchema>;

const UserProfile: React.FC = () => {
  const { user, token } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState<'profile' | 'bank'>('profile');

  // Set page metadata
  usePageMetadata('profile');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Use RTK Query for profile
  const { data: profile, refetch, isLoading: isProfileLoading } = useGetProfileQuery({ role: user?.role || 'USER' });
  const [updateProfile, { isLoading: isUpdating }] = useUpdateProfileMutation();
  const [updateAccountDetails, { isLoading: isUpdatingAccount }] = useUpdateUserAccountDetailsMutation();

  const profileForm = useForm<ProfileFormData>({
    resolver: yupResolver(profileSchema),
    defaultValues: {
      name: '',
      email: '',
      country_code: '',
    },
  });

  const bankAccountForm = useForm<yup.InferType<typeof bankAccountSchema>>({
    resolver: yupResolver(bankAccountSchema),
    defaultValues: {
      account_holder_name: '',
      bank_name: '',
      account_number: '',
    },
  });

  useEffect(() => {
    if (profile?.data) {
      profileForm.reset({
        name: profile.data.name || '',
        email: profile.data.email || '',
        country_code: profile.data.selected_country || '',
      });
    }
    if (profile?.user_account) {
      bankAccountForm.reset({
        account_holder_name: profile.user_account.account_holder_name || '',
        bank_name: profile.user_account.bank_name || '',
        account_number: profile.user_account.account_number || '',
      });
    }
  }, [profile, profileForm, bankAccountForm]);

  const handleProfileUpdate = async (data: ProfileFormData) => {
    try {
      const formData = new FormData();

      // API expects: name, email, country_code, and optional file
      formData.append('name', data.name);
      formData.append('email', data.email);
      if (data.country_code) {
        formData.append('country_code', data.country_code);
      }

      // Add file only if selected
      if (selectedFile) {
        formData.append('file', selectedFile);
      }

      await updateProfile({ formData, role: user?.role }).unwrap();
      toast.success('Profile updated successfully!');
      refetch();
      setSelectedFile(null);
      dispatch(addNotification({
        title: 'Profile Updated',
        message: 'Your profile information has been successfully updated.',
        type: 'success',
      }));
    } catch (error: any) {
      toast.error(error?.data?.message || error?.message || 'Failed to update profile');
    }
  };

  const handleBankAccountUpdate = async (data: yup.InferType<typeof bankAccountSchema>) => {
    try {
      await updateAccountDetails({
        account_holder_name: data.account_holder_name,
        bank_name: data.bank_name,
        account_number: data.account_number,
      }).unwrap();
      toast.success('Bank account details updated successfully!');
      refetch();
      dispatch(addNotification({
        title: 'Bank Account Updated',
        message: 'Your bank account information has been successfully updated.',
        type: 'success',
      }));
    } catch (error: any) {
      toast.error(error?.data?.message || error?.message || 'Failed to update bank account details');
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const TabButton: React.FC<{
    icon: React.ReactNode;
    label: string;
    isActive: boolean;
    onClick: () => void;
  }> = ({ icon, label, isActive, onClick }) => (
    <button
      onClick={onClick}
      className={`flex items-center space-x-2 px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm font-medium transition-colors ${isActive
        ? 'bg-blue-100 text-blue-700 border border-blue-200'
        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
        }`}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );

  if (!user) {
    return (
      <>
        <Navbar />
        <div className="flex items-center justify-center h-64">
          <div className="text-red-600 text-sm">Please log in to view your profile</div>
        </div>
      </>
    );
  }

  if (isProfileLoading) {
    return (
      <>
        <Navbar />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </>
    );
  }

  return (
      <>
        <Navbar />
        <div className="space-y-4 max-w-7xl mx-auto px-4 py-6">
      {/* Back Button */}
      <div className="mb-4">
        <Button
          variant="outline"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">User Profile</h1>
          <p className="text-sm text-gray-600">Manage your account information and preferences</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* User Info Card */}
        <div className="lg:col-span-1">
          <Card className="p-4">
            <div className="text-center space-y-4">
              {/* Avatar Section */}
              <div className="relative mx-auto">
                <UserAvatar 
                  src={
                    selectedFile
                      ? URL.createObjectURL(selectedFile)
                      : profile?.data?.profile_image
                  }
                  name={profile?.data?.name || user?.name}
                  size="xl"
                  className="mx-auto border-4 border-white shadow-lg"
                />
                <label className="absolute bottom-0 right-0 bg-blue-600 text-white p-1.5 rounded-full hover:bg-blue-700 transition-colors cursor-pointer shadow-lg">
                  <Camera className="h-3 w-3" />
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                </label>
              </div>

              {/* User Details */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-gray-900">
                  {profile?.data?.name || user?.name || 'User Name'}
                </h3>
                <p className="text-sm text-gray-600">
                  {profile?.data?.email || user?.email || 'user@example.com'}
                </p>
                <div className="flex justify-center">
                  <Badge variant="secondary" className="text-xs">User</Badge>
                </div>
              </div>

              {/* File Upload Info */}
              <div className="text-xs text-gray-500 space-y-1">
                <p>Click camera icon to change profile picture</p>
                <p>JPG, GIF or PNG. 1MB max.</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-2">
          <Card className="p-4">
            {/* Tab Navigation */}
            <div className="flex flex-wrap gap-2 mb-6">
              <TabButton
                icon={<User className="w-4 h-4" />}
                label="Profile Info"
                isActive={activeTab === 'profile'}
                onClick={() => setActiveTab('profile')}
              />
              <TabButton
                icon={<CreditCard className="w-4 h-4" />}
                label="Bank Account"
                isActive={activeTab === 'bank'}
                onClick={() => setActiveTab('bank')}
              />
            </div>

            {/* Tab Content */}
            <div className="space-y-4">
              {/* Profile Info Tab */}
              {activeTab === 'profile' && (
                <form onSubmit={profileForm.handleSubmit(handleProfileUpdate)} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Full Name *
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          {...profileForm.register('name')}
                          placeholder="Enter your full name"
                          className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                      </div>
                      {profileForm.formState.errors.name && (
                        <p className="text-xs text-red-500 mt-1">{profileForm.formState.errors.name.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Email Address *
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="email"
                          {...profileForm.register('email')}
                          placeholder="Enter your email"
                          className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                      </div>
                      {profileForm.formState.errors.email && (
                        <p className="text-xs text-red-500 mt-1">{profileForm.formState.errors.email.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Country Code
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          {...profileForm.register('country_code')}
                          placeholder="e.g., NG, US, UK"
                          className="w-full pl-3 pr-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Optional: Country code for currency</p>
                    </div>
                  </div>

                  {/* Sticky bottom buttons */}
                  <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-200">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate(-1)}
                      className="w-full sm:w-auto text-sm px-4 py-2"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={isUpdating}
                      className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-sm px-4 py-2"
                    >
                      {isUpdating ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </form>
              )}

              {/* Bank Account Tab */}
              {activeTab === 'bank' && (
                <form onSubmit={bankAccountForm.handleSubmit(handleBankAccountUpdate)} className="space-y-4">
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <Building2 className="w-5 h-5 text-gray-600 mt-0.5" />
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 mb-1">Bank Account Information</h3>
                        <p className="text-xs text-gray-600">
                          Manage your bank account details for transactions and withdrawals.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Account Holder Name *
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          {...bankAccountForm.register('account_holder_name')}
                          placeholder="Enter account holder name"
                          className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent text-sm"
                        />
                      </div>
                      {bankAccountForm.formState.errors.account_holder_name && (
                        <p className="text-xs text-red-500 mt-1">{bankAccountForm.formState.errors.account_holder_name.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Bank Name *
                      </label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          {...bankAccountForm.register('bank_name')}
                          placeholder="Enter bank name"
                          className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent text-sm"
                        />
                      </div>
                      {bankAccountForm.formState.errors.bank_name && (
                        <p className="text-xs text-red-500 mt-1">{bankAccountForm.formState.errors.bank_name.message}</p>
                      )}
                    </div>

                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Account Number *
                      </label>
                      <div className="relative">
                        <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          {...bankAccountForm.register('account_number')}
                          placeholder="Enter account number"
                          className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent text-sm"
                        />
                      </div>
                      {bankAccountForm.formState.errors.account_number && (
                        <p className="text-xs text-red-500 mt-1">{bankAccountForm.formState.errors.account_number.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Sticky bottom buttons */}
                  <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-200">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate(-1)}
                      className="w-full sm:w-auto text-sm px-4 py-2"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={isUpdatingAccount}
                      className="w-full sm:w-auto bg-gray-800 hover:bg-gray-900 text-sm px-4 py-2"
                    >
                      {isUpdatingAccount ? 'Saving...' : 'Save Bank Details'}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </Card>
        </div>
      </div>
      </div>
    </>
  );
};

export default UserProfile;
